import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Pencil, Rocket, Save, Send } from "lucide-react";
import {
  ActionBar,
  Alert,
  Button,
  Card,
  ConfirmDialog,
  ErrorState,
  FormStack,
  Input,
  LoadingState,
  Page,
  Badge,
  Tooltip,
} from "@marekl/adminer/components";
import { notifySuccess } from "@marekl/adminer/api";
import {
  UnsavedChangesModal,
  useSetTopbarCrumbs,
  useUnsavedChangesGuard,
} from "@marekl/adminer/shell";
import { useIsAdmin } from "../../auth/useAuth";
import {
  usePublishTemplate,
  useTemplateDetail,
  useUpdateTemplateDraft,
} from "../../lib/queries/templates";
import { KindBadge } from "../TemplatesPage/TemplatesPage";
import {
  buildDraftUpdate,
  buildPreviewRequest,
  initEditorState,
  parseSampleData,
  snapshot,
  type EditorState,
} from "./editorState";
import { BlocksEditor } from "./BlocksEditor";
import { CodeTemplateEditor } from "./CodeTemplateEditor";
import { ThemePanel } from "./ThemePanel";
import { SampleDataPanel } from "./SampleDataPanel";
import { PreviewPane } from "./PreviewPane";
import { TestSendModal } from "./TestSendModal";
import { TemplateDetailsModal } from "./TemplateDetailsModal";
import { PartialsSection } from "./PartialsSection";
import { VersionsSection } from "./VersionsSection";
import styles from "./TemplateDetailPage.module.css";

/**
 * Full-page template editor: header with save/publish/test actions, left
 * editor column (blocks WYSIWYG or code tabs), right live preview, plus
 * partials (code templates) and version history.
 */
export function TemplateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const isAdmin = useIsAdmin();
  const detail = useTemplateDetail(id);
  const saveDraft = useUpdateTemplateDraft(id);
  const publish = usePublishTemplate(id);

  const [state, setState] = useState<EditorState | null>(null);
  const [baseline, setBaseline] = useState("");
  const [publishOpen, setPublishOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const lastValidDataRef = useRef<unknown>({});

  // The root crumb is part of every trail (AppLayout puts it on the default
  // ones); setting a trail here replaces the whole thing, so it has to carry it.
  useSetTopbarCrumbs([
    { label: "Letterman", href: "/dashboard" },
    { label: "Templates", href: "/templates" },
    { label: detail.data?.name ?? "Detail" },
  ]);

  const template = detail.data;
  const kind = template?.kind;
  // An SMS template is one text body: no subject, preheader, HTML or theme.
  const isSMS = template?.channel === "sms";
  const channel = isSMS ? ("sms" as const) : ("email" as const);

  // Initialize the editor once per template; refetches (e.g. after a
  // save-triggered invalidation) must not clobber in-progress edits.
  const initializedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (template && initializedForRef.current !== template.id) {
      initializedForRef.current = template.id;
      const initial = initEditorState(template);
      setState(initial);
      setBaseline(snapshot(initial, template.kind));
    }
  }, [template]);

  const dirty = useMemo(
    () => (state && kind ? snapshot(state, kind) !== baseline : false),
    [state, kind, baseline],
  );

  // Holds in-app navigation while dirty (the app is on a data router, so
  // useBlocker is available) and warns on tab close / reload.
  const guard = useUnsavedChangesGuard(dirty);

  const sampleError = useMemo(
    () => (state ? parseSampleData(state.sampleData).error : null),
    [state],
  );

  const previewRequest = useMemo(() => {
    if (!state || !kind) return null;
    const { data, error } = parseSampleData(state.sampleData);
    if (!error) lastValidDataRef.current = data;
    return buildPreviewRequest(
      state,
      kind,
      channel,
      error ? lastValidDataRef.current : data,
      false,
    );
  }, [state, kind, channel]);

  function patch(p: Partial<EditorState>) {
    setState((s) => (s ? { ...s, ...p } : s));
  }

  async function handleSave() {
    if (!state || !kind) return;
    try {
      const version = await saveDraft.mutateAsync(buildDraftUpdate(state, kind));
      notifySuccess(`Draft saved as draft version ${version.version}.`);
      setBaseline(snapshot(state, kind));
    } catch {
      // global mutation error toast already shown
    }
  }

  async function handlePublish() {
    try {
      const version = await publish.mutateAsync();
      notifySuccess(`Version ${version.version} was published.`);
      setPublishOpen(false);
    } catch {
      // Shown by the ConfirmDialog error slot (the mutation suppresses the toast).
    }
  }

  if (detail.isLoading) {
    return (
      <Page>
        <Page.Content>
          <LoadingState label="Loading template…" />
        </Page.Content>
      </Page>
    );
  }

  if (detail.error || !template) {
    return (
      <Page>
        <Page.Content>
          <ErrorState
            error={detail.error ?? "The template was not found."}
            title="Failed to load the template."
            onRetry={detail.refetch}
          />
          <p>
            <Link to="/templates">← Back to templates</Link>
          </p>
        </Page.Content>
      </Page>
    );
  }

  return (
    <Page>
      <Page.Header
        title={template.name}
        subtitle={template.key}
        subtitleVariant="default"
        actions={
          <>
            <KindBadge kind={template.kind} />
            {isAdmin && (
              <Button size="sm" onClick={() => setDetailsOpen(true)}>
                <Pencil size={16} aria-hidden="true" />
                Edit details
              </Button>
            )}
          </>
        }
      />
      <Page.Content stack gap="lg">
        {state && (
          <div className={styles.editorLayout}>
            <div className={styles.editorColumn}>
              {template.kind === "blocks" ? (
                <>
                  <Card>
                    <Card.Header title="Subject and preheader" />
                    <Card.Content>
                      <FormStack>
                        <Input
                          label="Subject"
                          value={state.subject}
                          onChange={(e) => patch({ subject: e.target.value })}
                          hint="Go template — data is available as {{.Data.field}}."
                        />
                        <Input
                          label="Preheader"
                          value={state.preheader}
                          onChange={(e) => patch({ preheader: e.target.value })}
                          hint="Short text shown in inbox previews after the subject."
                        />
                      </FormStack>
                    </Card.Content>
                  </Card>
                  <BlocksEditor
                    doc={state.doc}
                    onChange={(doc) => patch({ doc })}
                  />
                  <ThemePanel
                    theme={state.theme}
                    onChange={(theme) => patch({ theme })}
                  />
                </>
              ) : (
                <CodeTemplateEditor state={state} onChange={patch} isSMS={isSMS} />
              )}
              <SampleDataPanel
                value={state.sampleData}
                onChange={(sampleData) => patch({ sampleData })}
                error={sampleError}
              />
            </div>

            <div className={styles.previewColumn}>
              <PreviewPane
                request={previewRequest}
                sampleDataWarning={
                  sampleError
                    ? "The sample data is not valid JSON — the preview uses the last valid data."
                    : null
                }
              />
            </div>
          </div>
        )}

        {template.kind === "code" && <PartialsSection />}

        <VersionsSection
          templateId={template.id}
          kind={template.kind}
          channel={channel}
          sampleData={lastValidDataRef.current}
        />

        {/* Sticky editor bar: state pills inline-start, save/publish/test
            inline-end. Stays mounted through its quiescent state (it is a
            draft bar, not a BulkActionBar). */}
        <ActionBar
          aria-label="Template editor status and actions"
          startLayout="row"
          start={
            <>
              {template.draft && (
                <Badge weight="ghost" variant="warn" dot>
                  Draft version {template.draft.version}
                </Badge>
              )}
              {template.current && (
                <Badge weight="ghost" variant="ok" dot>
                  Published version {template.current.version}
                </Badge>
              )}
              {!template.has_published && (
                <Badge weight="ghost" variant="muted" dot>Not published yet</Badge>
              )}
              {dirty && (
                <Badge weight="ghost" variant="err" dot>Unsaved changes</Badge>
              )}
            </>
          }
        >
          {isAdmin && (
            <>
              <Button size="sm" variant="primary" weight="outline" onClick={() => setTestOpen(true)}>
                <Send size={16} aria-hidden="true" />
                Test email
              </Button>
              {/* Empty Tooltip content renders no tip, so only the disabled
                  reason ever shows. The span carries the hover handlers — a
                  disabled button does not emit mouse events. */}
              <Tooltip
                content={
                  template.draft
                    ? undefined
                    : "Nothing to publish — the template has no draft."
                }
              >
                <span className={styles.headerActionWrap}>
                  <Button
                    size="sm"
                    variant="primary"
                    weight="outline"
                    disabled={!template.draft}
                    onClick={() => {
                      publish.reset();
                      setPublishOpen(true);
                    }}
                  >
                    <Rocket size={16} aria-hidden="true" />
                    Publish
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                content={
                  sampleError
                    ? "The sample data is not valid JSON."
                    : !dirty
                      ? "No unsaved changes."
                      : undefined
                }
              >
                <span className={styles.headerActionWrap}>
                  <Button
                    size="sm"
                    variant="primary"
                    loading={saveDraft.isPending}
                    disabled={!dirty || !!sampleError}
                    onClick={() => void handleSave()}
                  >
                    <Save size={16} aria-hidden="true" />
                    Save
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
        </ActionBar>

        <UnsavedChangesModal
          open={guard.blocked}
          onConfirm={guard.proceed}
          onClose={guard.cancel}
        />

        <ConfirmDialog
          open={publishOpen}
          title="Publish template"
          confirmLabel="Publish"
          busy={publish.isPending}
          error={publishOpen ? publish.error ?? undefined : undefined}
          onConfirm={() => void handlePublish()}
          onClose={() => setPublishOpen(false)}
        >
          {dirty && (
            <Alert variant="warn">
              You have unsaved changes — the last saved draft is what gets published.
            </Alert>
          )}
          <p>
            Publish draft version{" "}
            <strong>{template.draft?.version}</strong>? Newly sent messages
            will start using it.
          </p>
        </ConfirmDialog>

        {detailsOpen && (
          <TemplateDetailsModal template={template} onClose={() => setDetailsOpen(false)} />
        )}

        {testOpen && state && (
          <TestSendModal
            templateId={template.id}
            channel={template.channel}
            sampleData={state.sampleData}
            onClose={() => setTestOpen(false)}
          />
        )}
      </Page.Content>
    </Page>
  );
}
